/**
 * /free-audit — Free Savings Audit lead-capture landing page.
 *
 * v35.70 (Jul 2026) — the founder's outbound weapon. Single-CTA page:
 * "We'll find the waste in your cloud + LLM bill within 48 hours, or you
 * never hear from us again." Minimal header (no full nav — outbound landing
 * pages convert better without exits), honest trust strip, one form.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Clock, FileSearch, Lock, ShieldCheck,
  Sparkles, TrendingDown, Send,
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLOUD_BRACKETS = ['Under $5k / mo', '$5k – $25k / mo', '$25k – $100k / mo', '$100k+ / mo'];
const LLM_BRACKETS = ['Under $1k / mo', '$1k – $10k / mo', '$10k – $50k / mo', '$50k+ / mo', 'Not sure'];

const STEPS = [
  { icon: Send, title: '1 · Tell us where it hurts', body: '60 seconds. Name, email, rough spend. No card, no call required.' },
  { icon: FileSearch, title: '2 · We run the 26 engines', body: 'Token waste, idle resources, unattributed spend, cache leaks — priced line by line.' },
  { icon: TrendingDown, title: '3 · You get the report in 48h', body: 'Top 10 leaks with dollar amounts and exact fixes. Keep it either way — it\u2019s yours.' },
];

export default function FreeAudit() {
  const [form, setForm] = useState({ name: '', email: '', company: '', cloud_spend: '', llm_spend: '', biggest_pain: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      setError('Name, work email and company are required.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/free-audit/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Request failed');
      setDone(true);
    } catch {
      setError('Something went wrong — please try again or email hello@cartieai.com.');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 rounded-lg bg-slate-900 border border-white/10 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none text-sm';

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="free-audit-page">
      <Helmet>
        <title>Free Cloud + AI Bill Audit · CARTIEAI</title>
        <meta name="description" content="We'll find the waste in your cloud and LLM bill within 48 hours — or you never hear from us again. Free, no card, no sales call." />
      </Helmet>

      {/* Minimal header — logo home + one escape hatch to the live demo */}
      <header className="border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="free-audit-logo-link">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-9 h-9 object-contain" />
            <span className="text-lg font-bold text-white">CARTIE<span className="text-emerald-400">AI</span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super">™</sup></span>
          </Link>
          <Link to="/try" className="text-sm text-slate-400 hover:text-emerald-300 transition-colors flex items-center gap-1.5" data-testid="free-audit-try-link">
            Prefer instant proof? Try the live demo <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-14 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left — the pitch */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-bold tracking-wider uppercase mb-6" data-testid="free-audit-badge">
              <Sparkles className="w-3.5 h-3.5" /> Free · No card · No sales call
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              We&rsquo;ll find the waste in your <span className="text-emerald-400">cloud + AI bill</span> in 48 hours.
            </h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">
              Or you never hear from us again. That&rsquo;s the whole deal — a personal audit of your spend,
              run through CARTIEAI&rsquo;s 26 engines, delivered as a priced report you keep either way.
            </p>

            <div className="mt-10 space-y-5">
              {STEPS.map((s) => (
                <div key={s.title} className="flex gap-4 items-start" data-testid={`free-audit-step-${s.title[0]}`}>
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm">{s.title}</p>
                    <p className="text-sm text-slate-400 mt-0.5">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Honest trust strip */}
            <div className="mt-10 p-5 rounded-xl bg-white/[0.03] border border-white/[0.08] space-y-3" data-testid="free-audit-trust-strip">
              <div className="flex items-center gap-2.5 text-sm text-slate-300"><Lock className="w-4 h-4 text-emerald-400 shrink-0" /> Read-only access only — we can see your bill, never touch your infra.</div>
              <div className="flex items-center gap-2.5 text-sm text-slate-300"><ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" /> SOC 2 controls published &amp; verifiable at <Link to="/trust" className="text-emerald-400 hover:underline">/trust</Link>.</div>
              <div className="flex items-center gap-2.5 text-sm text-slate-300"><Clock className="w-4 h-4 text-emerald-400 shrink-0" /> If you later subscribe: 3× ROI in 90 days, contractual — <Link to="/guarantee" className="text-emerald-400 hover:underline">the guarantee</Link>.</div>
            </div>
          </div>

          {/* Right — the form */}
          <div className="lg:sticky lg:top-10">
            {done ? (
              <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-8 text-center" data-testid="free-audit-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-2xl font-extrabold text-white">You&rsquo;re in. The clock starts now.</h2>
                <p className="mt-3 text-slate-300 text-sm">
                  Your audit request landed. You&rsquo;ll get your priced waste report at
                  <span className="text-emerald-300 font-semibold"> {form.email} </span>
                  within 48 hours.
                </p>
                <p className="mt-4 text-slate-400 text-sm">Can&rsquo;t wait? See the engines work on a live bill right now:</p>
                <Link to="/try" data-testid="free-audit-success-try-btn"
                  className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm transition-colors">
                  Run the 60-second live demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 sm:p-8 space-y-4" data-testid="free-audit-form">
                <h2 className="text-xl font-extrabold text-white">Claim your free audit</h2>
                <p className="text-sm text-slate-400 -mt-2">60 seconds. Three required fields. Zero commitment.</p>

                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Name *</label>
                  <input className={inputCls} placeholder="Jane Nakamura" value={form.name} onChange={set('name')} data-testid="free-audit-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Work email *</label>
                  <input type="email" className={inputCls} placeholder="jane@acme.com" value={form.email} onChange={set('email')} data-testid="free-audit-email-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Company *</label>
                  <input className={inputCls} placeholder="Acme Corp" value={form.company} onChange={set('company')} data-testid="free-audit-company-input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Monthly cloud spend</label>
                    <select className={inputCls} value={form.cloud_spend} onChange={set('cloud_spend')} data-testid="free-audit-cloud-spend-select">
                      <option value="">Select…</option>
                      {CLOUD_BRACKETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Monthly LLM / AI spend</label>
                    <select className={inputCls} value={form.llm_spend} onChange={set('llm_spend')} data-testid="free-audit-llm-spend-select">
                      <option value="">Select…</option>
                      {LLM_BRACKETS.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1.5">Biggest cost pain <span className="text-slate-500 normal-case font-normal">(optional)</span></label>
                  <textarea rows={3} className={inputCls} placeholder='e.g. "Our OpenAI bill tripled last quarter and nobody knows why."' value={form.biggest_pain} onChange={set('biggest_pain')} data-testid="free-audit-pain-input" />
                </div>

                {error && <p className="text-sm text-rose-400" data-testid="free-audit-error">{error}</p>}

                <button type="submit" disabled={busy} data-testid="free-audit-submit-btn"
                  className="w-full py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-slate-950 font-extrabold text-sm transition-colors flex items-center justify-center gap-2">
                  {busy ? 'Sending…' : <>Get my free audit <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[11px] text-slate-500 text-center">We reply personally within 48h. Your email is never shared or added to a drip sequence.</p>
              </form>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
